(() => {
  const OEMATCH_HANDLE = 'oematch-paint-all-oem-color-codes';
  const SEARCH_ENDPOINT = '/tss-genie/tss-oem-colors/v2/search.php';
  const originalFetch = window.fetch.bind(window);
  const pageParams = new URLSearchParams(window.location.search);
  const completeKitMode = pageParams.get('kit') === 'complete';
  const requestedFormula = pageParams.get('formula')?.trim().toLowerCase() || '';
  const requestedRecord = pageParams.get('record')?.trim() || '';
  const requestedColorId = pageParams.get('color_id')?.trim() || '';
  const requestedRtsVariant = pageParams.get('rts_variant')?.trim() || '';
  const requestedNonReducedVariant = pageParams.get('nonreduced_variant')?.trim() || '';

  const readKitCatalog = () => {
    try {
      return JSON.parse(document.querySelector('[data-oematch-kit-catalog]')?.textContent || '{}');
    } catch (error) {
      console.warn('[Linear OEMatch] Could not read the complete-kit catalog.', error);
      return {};
    }
  };

  const availableVariants = (product) => (product?.variants || []).filter((variant) => variant.available);

  const findVariant = (product, requiredText = []) => {
    const tokens = requiredText.map((value) => String(value).toLowerCase());
    return availableVariants(product).find((variant) => tokens.every((token) => variant.title.toLowerCase().includes(token))) || null;
  };

  // Speed tables live in assets/linear-kit-sizing.js. Clearcoat runs a step
  // slower than reducer on purpose; do not collapse these back into one call.
  const kitSpeed = (role) => window.LinearKitSizing.speedFor(role || 'reducer', pageParams.get('temperature'));

  const kitSealerColor = () => {
    const color = pageParams.get('color')?.toLowerCase() || '';
    if (/black|void|phantom|luxor/.test(color)) return 'Black';
    if (/white|cream|ivory|yellow|orange/.test(color)) return 'White';
    return 'Gray';
  };

  // Quantities come from assets/linear-kit-sizing.js, the same table the paint
  // kit builder uses. Do not reintroduce a local copy of these rules.
  const kitPlan = () => window.LinearKitSizing.plan({
    basecoatPreparation: pageParams.get('basecoatPreparation'),
    size: pageParams.get('size'),
    coverage: pageParams.get('coverage'),
    clearcoatBuild: pageParams.get('clearcoatBuild'),
    condition: pageParams.get('condition'),
  });

  const kitBasecoatQuantity = () => kitPlan().gallons;
  const kitReducerQuantity = () => kitPlan().reducerPackages;
  const kitClearcoatQuantity = () => kitPlan().clearcoatPackages;

  const kitVariant = (role, product) => {
    const speed = kitSpeed('reducer');
    if (role === 'sealer') return findVariant(product, ['4.5 Liter', kitSealerColor()]);
    if (role === 'reducer') return findVariant(product, ['5L', speed]);
    if (role === 'clearcoat') return findVariant(product, ['7.5L', kitSpeed('clearcoat')]);
    if (role === 'primer') return findVariant(product, [kitSealerColor(), 'Gallon Kit']) || findVariant(product, ['Gray', 'Gallon Kit']);
    if (role === 'masking') return findVariant(product, ['12 (Sleeve)']);
    if (role === 'masking-film') return findVariant(product, ['180cm']);
    return findVariant(product);
  };

  const appendCompleteKit = (payload) => {
    if (!completeKitMode || !Array.isArray(payload?.items)) return payload;
    const oemItems = payload.items.filter((item) => item?.properties?._tss_source === 'embed/v1');
    if (!oemItems.length || payload.items.some((item) => item?.properties?.['_Kit ID'])) return payload;

    const basecoatItem = oemItems.find((item) => item?.properties?.Make !== 'UNDERCOAT') || oemItems[0];
    const actualFormula = String(basecoatItem?.properties?._formula_id || '').trim().toLowerCase();
    if (requestedFormula && actualFormula !== requestedFormula) {
      throw new Error('The selected OEMatch formula does not match the featured project.');
    }
    const actualColorId = String(basecoatItem?.properties?._color_id || '').trim();
    if (requestedColorId && actualColorId !== requestedColorId) {
      throw new Error('The selected OEMatch color record does not match the featured project.');
    }

    const requestedPreparation = pageParams.get('basecoatPreparation');
    const actualPreparation = String(basecoatItem?.properties?.Reducer || '').trim().toLowerCase();
    const actualSize = String(basecoatItem?.properties?.Size || '').trim().toLowerCase();
    if (!actualSize.includes('gallon') || !actualSize.includes('128oz')) {
      throw new Error('Complete featured-project kits require the gallon OEMatch package.');
    }
    if (requestedPreparation === 'Non-reduced' && !actualPreparation.includes('non-reduced')) {
      throw new Error('The selected OEMatch basecoat is not configured as non-reduced.');
    }
    if (requestedPreparation === 'Ready to spray' && !actualPreparation.includes('ready to spray')) {
      throw new Error('The selected OEMatch basecoat is not configured as ready to spray.');
    }
    const expectedVariant = requestedPreparation === 'Non-reduced'
      ? requestedNonReducedVariant
      : requestedRtsVariant;
    if (expectedVariant && String(basecoatItem.id) !== expectedVariant) {
      throw new Error('The selected OEMatch package does not match this featured project.');
    }

    const catalog = readKitCatalog();
    const needsBodywork = window.LinearKitSizing.needsBodywork(pageParams.get('condition'));
    const fullColorChange = pageParams.get('coverage') === 'Full color change';
    const kitId = `linear-oem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const baseProperties = {
      '_Kit ID': kitId,
      'Paint color': pageParams.get('color') || basecoatItem.properties['Color name'] || 'Selected OEM color',
      'Vehicle size': pageParams.get('size') || 'Not specified',
      'Coverage': pageParams.get('coverage') || 'Not specified',
      'Condition': pageParams.get('condition') || 'Not specified',
      'Booth temperature': pageParams.get('temperature') || 'Not specified',
      'Graphics choice': pageParams.get('graphics') || 'Not specified',
      'Basecoat preparation': basecoatItem.properties.Reducer || pageParams.get('basecoatPreparation') || 'Configured in OEMatch'
    };

    oemItems.forEach((item) => {
      const isUndercoat = item.properties.Make === 'UNDERCOAT';
      if (item === basecoatItem) item.quantity = kitBasecoatQuantity();
      item.properties = {
        ...item.properties,
        ...baseProperties,
        '_Kit role': isUndercoat ? 'oem-undercoat' : 'oem-basecoat'
      };
    });

    const entries = [
      { role: 'sealer', product: catalog.sealer, include: true, required: true },
      { role: 'reducer', product: catalog.reducer, include: true, required: true },
      { role: 'clearcoat', product: catalog.clearcoat, include: true, required: true },
      { role: 'primer', product: catalog.primer, include: needsBodywork, required: needsBodywork },
      { role: 'bodywork', product: catalog.filler, include: needsBodywork, required: false },
      { role: 'masking', product: catalog.masking, include: true, required: false },
      { role: 'masking-film', product: catalog.maskingFilm, include: true, required: false },
      { role: 'cleaning-cloths', product: catalog.microfiber, include: true, required: false },
      { role: 'foam-masking', product: catalog.foamTape, include: fullColorChange, required: false },
      { role: 'trim-masking', product: catalog.trimTape, include: fullColorChange, required: false }
    ];

    const unresolvedRequired = entries.filter((entry) => entry.required && !kitVariant(entry.role, entry.product));
    if (unresolvedRequired.length) {
      throw new Error(`Missing exact complete-kit packages: ${unresolvedRequired.map((entry) => entry.role).join(', ')}`);
    }

    entries.filter((entry) => entry.include).forEach((entry) => {
      const variant = kitVariant(entry.role, entry.product);
      if (!variant) return;
      payload.items.push({
        id: variant.id,
        quantity: entry.role === 'reducer' ? kitReducerQuantity()
          : entry.role === 'clearcoat' ? kitClearcoatQuantity()
          : 1,
        properties: { ...baseProperties, '_Kit role': entry.role }
      });
    });
    return payload;
  };

  const normalize = (value) => String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const scoreResult = (result, query) => {
    const code = normalize(result.color_code || result.code);
    const name = normalize(result.color_name || result.name);
    const make = normalize(result.make);
    const normalizedQuery = normalize(query);
    const tokens = normalizedQuery.split(' ').filter(Boolean);
    const searchable = `${make} ${name} ${code}`;

    if (!normalizedQuery) return 0;
    if (code === normalizedQuery) return 100;
    if (name === normalizedQuery) return 95;
    if (`${name} ${code}` === normalizedQuery || `${code} ${name}` === normalizedQuery) return 90;
    if (name.startsWith(normalizedQuery)) return 85;
    if (code.startsWith(normalizedQuery)) return 80;
    if (tokens.length > 1 && tokens.every((token) => searchable.includes(token))) return 70;
    if (name.includes(normalizedQuery)) return 60;
    if (searchable.includes(normalizedQuery)) return 50;
    return 0;
  };

  const formulaKey = (result, index) => {
    const source = normalize(result.source) || 'OEM';
    const formula = normalize(result.formula_id);
    if (formula) return `${source}:FORMULA:${formula}`;
    if (result.color_id) return `${source}:COLOR:${result.color_id}`;

    const code = normalize(result.color_code || result.code);
    const name = normalize(result.color_name || result.name);
    if (code || name) return `${source}:COLOR-NAME:${code}:${name}`;

    return `${source}:RECORD:${result.id || index}`;
  };

  const summarizeMakes = (makes) => {
    const unique = [...new Set(makes.filter(Boolean))];
    if (unique.length <= 2) return unique.join(' / ');
    return `${unique.slice(0, 2).join(' / ')} +${unique.length - 2} makes`;
  };

  const condenseSearchResults = (results, query) => {
    const ranked = results
      .filter((result) => result && result.source !== 'custom' && normalize(result.make) !== 'ALPHA AUTOMOTIVE PAINT')
      .map((result, index) => ({ result, index, score: scoreResult(result, query) }))
      .sort((left, right) => right.score - left.score || left.index - right.index);

    const bestScore = ranked[0]?.score || 0;
    const relevant = bestScore >= 70 ? ranked.filter((entry) => entry.score === bestScore) : ranked;
    const formulas = new Map();

    relevant.forEach(({ result, index }) => {
      const key = formulaKey(result, index);
      const make = String(result.make || '').trim();
      const existing = formulas.get(key);

      if (!existing) {
        formulas.set(key, { representative: { ...result }, makes: make ? [make] : [] });
        return;
      }

      if (make && !existing.makes.includes(make)) existing.makes.push(make);
    });

    return [...formulas.values()].map(({ representative, makes }) => ({
      ...representative,
      make: summarizeMakes(makes) || representative.make,
      make_aliases: makes
    }));
  };

  window.fetch = async (...args) => {
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    let fetchArgs = args;
    if (completeKitMode && /\/cart\/add(?:\.js)?(?:\?|$)/.test(requestUrl)) {
      try {
        const options = { ...(args[1] || {}) };
        const payload = appendCompleteKit(JSON.parse(options.body || '{}'));
        options.body = JSON.stringify(payload);
        fetchArgs = [args[0], options];
      } catch (error) {
        console.warn('[Linear OEMatch] Could not append the complete kit.', error);
        return new Response(JSON.stringify({ description: 'The complete Linear system could not be configured. Please contact Linear for help.' }), {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        });
      }
    }
    const response = await originalFetch(...fetchArgs);
    if (!requestUrl.includes(SEARCH_ENDPOINT) || !response.ok) return response;

    try {
      const parsedUrl = new URL(requestUrl, window.location.origin);
      const query = parsedUrl.searchParams.get('q') || '';
      const payload = await response.clone().json();
      if (!Array.isArray(payload)) return response;

      const formulaLocked = requestedFormula
        ? payload.filter((result) => String(result?.formula_id || '').trim().toLowerCase() === requestedFormula)
        : payload;
      const recordLocked = requestedRecord
        ? formulaLocked.filter((result) => String(result?.id || '').trim() === requestedRecord)
        : formulaLocked;
      const colorLocked = requestedColorId
        ? recordLocked.filter((result) => String(result?.color_id || '').trim() === requestedColorId)
        : recordLocked;
      const condensed = condenseSearchResults(colorLocked, query);
      const headers = new Headers(response.headers);
      headers.set('content-type', 'application/json; charset=utf-8');
      return new Response(JSON.stringify(condensed), {
        status: response.status,
        statusText: response.statusText,
        headers
      });
    } catch (error) {
      console.warn('[Linear OEMatch] Could not condense search results.', error);
      return response;
    }
  };

  const requestedColor = pageParams.get('oematch');
  if (!requestedColor || !window.location.pathname.endsWith(`/pages/${OEMATCH_HANDLE}`)) return;

  const renderKitNotice = () => {
    if (!completeKitMode || document.querySelector('[data-oematch-complete-kit-notice]')) return;
    const host = document.querySelector('[id^="tss-oem-embed-"]');
    if (!host) return;
    const notice = document.createElement('div');
    notice.dataset.oematchCompleteKitNotice = '';
    notice.style.cssText = 'max-width:120rem;margin:0 auto 2rem;padding:1.6rem 2rem;border-left:.5rem solid #4874df;background:#f0f3fb;color:#0b1d53;';
    notice.innerHTML = '<strong style="display:block;font-size:1.8rem;margin-bottom:.4rem">Complete-kit mode is on.</strong><span>Configure the OEM basecoat below. Add to cart will also add one package each of Alpha sealer, temperature-matched reducer, clearcoat, and the selected prep supplies. Vehicle size and coverage set your basecoat quantity. Have wax and grease remover plus tack cloths on hand before you spray.</span>';
    host.before(notice);
  };

  const prefillSearch = () => {
    const host = document.querySelector('[id^="tss-oem-embed-"]');
    const input = host?.shadowRoot?.querySelector('#oem-qs-code');
    if (!input) return false;

    input.value = requestedColor;
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    return true;
  };

  if (!prefillSearch()) {
    const observer = new MutationObserver(() => {
      renderKitNotice();
      if (prefillSearch()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  }
  renderKitNotice();
})();
